// A media caption and its credit read as one line:
//
//   Ruoru Mou, Offspring 2025: De Ateliers | De Relaties. Photo: Gert Jan van Rooij
//
// The caption is rich text (it carries links and emphasis) and the credit is
// plain text, so the two are joined by appending the credit into the caption's
// lexical document rather than by concatenating strings — everything the editor
// marked up in the caption survives.
//
// Editors end a caption with a period about half the time. Neither habit is
// wrong on its own, but the join has to land on exactly one, so the period
// between caption and credit is added or left alone here rather than typed.
// A caption with no credit is passed through as written.
//
// Credits are already stored labelled ("Photo: ...", "Foto: ...") — see
// payload/src/utilities/media-credit.ts.

/** Terminal punctuation, plus any closing quote or bracket that follows it. */
const SENTENCE_END = /[.!?…]["'’”)\]]*$/

const textNode = (text: string) => ({
  type: 'text',
  text,
  detail: 0,
  format: 0,
  mode: 'normal',
  style: '',
  version: 1,
})

const paragraphNode = (children: any[]) => ({
  type: 'paragraph',
  children,
  direction: 'ltr',
  format: '',
  indent: 0,
  textFormat: 0,
  version: 1,
})

const richTextDoc = (children: any[]) => ({
  root: {
    type: 'root',
    children,
    direction: 'ltr',
    format: '',
    indent: 0,
    version: 1,
  },
})

/** The caption's last piece of text, which is where it either ends in a period or doesn't. */
const lastTextOf = (node: any): string => {
  if (typeof node?.text === 'string') {
    return node.text.trimEnd()
  }

  if (Array.isArray(node?.children)) {
    for (let index = node.children.length - 1; index >= 0; index--) {
      const text = lastTextOf(node.children[index])

      if (text) {
        return text
      }
    }
  }

  return ''
}

// A trailing space the editor left behind would otherwise show up in front of
// the period we add. Clone along the path to the last text node rather than
// mutating the caption we were handed.
const trimEnding = (node: any): any | null => {
  if (typeof node?.text === 'string') {
    const text = node.text.trimEnd()

    if (!text) {
      return null
    }

    return text === node.text ? node : { ...node, text }
  }

  if (Array.isArray(node?.children)) {
    for (let index = node.children.length - 1; index >= 0; index--) {
      const updated = trimEnding(node.children[index])

      if (updated) {
        const children = [...node.children]
        children[index] = updated

        return { ...node, children }
      }
    }
  }

  return null
}

// The credit belongs on the caption's last line, so it goes inside the last
// block rather than after it. Anything that can't take children (a rule, an
// image) gets a paragraph of its own.
const appendToLastBlock = (root: any, node: any) => {
  const children = [...(root.children ?? [])]
  const lastIndex = children.length - 1
  const last = children[lastIndex]

  if (last && Array.isArray(last.children)) {
    children[lastIndex] = { ...last, children: [...last.children, node] }
  }
  else {
    children.push(paragraphNode([node]))
  }

  return { ...root, children }
}

/**
 * Caption and credit as a single lexical document, ready for `core-rich-text`,
 * or null when the media carries neither.
 */
export const captionWithCredit = (caption: any, credit?: string | null): any | null => {
  const creditText = typeof credit === 'string' ? credit.trim() : ''
  const hasCaption = hasRichTextContent(caption)

  if (!hasCaption) {
    return creditText ? richTextDoc([paragraphNode([textNode(creditText)])]) : null
  }

  if (!creditText) {
    return caption
  }

  const root = trimEnding(caption.root) ?? caption.root
  const separator = SENTENCE_END.test(lastTextOf(root)) ? ' ' : '. '

  return { ...caption, root: appendToLastBlock(root, textNode(`${separator}${creditText}`)) }
}
